import { CREDITS_MS, type ClosingSequence, type ClosingStep } from '../../game/closing-sequence';
import { CREDITS_PAGE_SIZE, creditEntries, creditSections, creditsTeaser } from '../../data/credits';
import { t, type StringKey } from '../../i18n/es';

export interface ClosingScreenOptions {
  sequence: ClosingSequence;
  /** Reduced motion (#63): the credits are paged instead of scrolling and nothing is animated. */
  reducedMotion: boolean;
  /** The sequence is over: back to the title. */
  onFinish: () => void;
}

/** How often the screen lets the sequence know that time has passed (ms). */
export const TICK_MS = 100;

const CAPTIONS: Partial<Record<ClosingStep, StringKey>> = {
  REVELACION: 'ui.cierre.caption.reveal',
  ESCAPE: 'ui.cierre.caption.escape',
  HARINA: 'ui.cierre.caption.flour',
};

/** The comic marks of each beat: the DEA badge with the name, the parachute jump and the seal of the flour (GDD §3.3, §3.4). */
function marks(step: ClosingStep): string {
  if (step === 'REVELACION') {
    return `<p class="badge" data-field="badge">${t('ui.cierre.badge.dea')}</p> <p class="display-mono" data-field="agent">${t('ui.cierre.badge.agent')}</p>`;
  }
  if (step === 'HARINA') return `<p class="badge" data-field="seal">${t('ui.cierre.seal.flour')}</p>`;
  return '';
}

function creditsMarkup(reducedMotion: boolean, page: number): string {
  if (reducedMotion) {
    const entries = creditEntries();
    const pages = Math.ceil(entries.length / CREDITS_PAGE_SIZE);
    const shown = entries.slice(page * CREDITS_PAGE_SIZE, (page + 1) * CREDITS_PAGE_SIZE);
    const last = page >= pages - 1;
    return `
      <div class="credits credits--paged" data-field="credits">
        <ul class="credits-list">${shown
          .map(({ title, entry }) => `<li data-entry="${entry.name}"><span class="tactical">${title}</span> <span class="body">${entry.name}</span> <span class="muted">${entry.detail}</span> <span class="readout" data-field="licence">${entry.licence}</span></li>`)
          .join('')}</ul>
        ${last ? `<p class="tactical" data-field="teaser">${creditsTeaser()}</p>` : ''}
        <p class="muted" data-field="page">${t('ui.cierre.page', { n: page + 1, max: pages })}</p>
        <button type="button" class="button" data-action="${last ? 'finish' : 'next'}">${last ? t('ui.cierre.back') : t('ui.cierre.next')}</button>
      </div>`;
  }
  return `
    <div class="credits" data-field="credits">
      <div class="credits-scroll" style="animation-duration: ${CREDITS_MS}ms">${creditSections()
        .map(
          (section) => `
        <h2 class="tactical">${section.title}</h2>
        <ul class="credits-list">${section.entries
          .map((entry) => `<li data-entry="${entry.name}"><span class="body">${entry.name}</span> <span class="muted">${entry.detail}</span> <span class="readout" data-field="licence">${entry.licence}</span></li>`)
          .join('')}</ul>`,
        )
        .join('')}
        <p class="tactical" data-field="teaser">${creditsTeaser()}</p>
      </div>
    </div>`;
}

/**
 * CIERRE (GDD §3.3, §13.2, issue #136): the three beats of the ending and the credits. It only reads the sequence of the game
 * (`ClosingSequence`) and draws its step; "Saltar" (always visible, with the initial focus) and Escape skip from any beat to the
 * credits and, in the credits, end the sequence. Flapping (Space, a click on the canvas) never skips it, so a flap cannot skip it
 * by accident. With reduced motion the credits are pages with a button, with no scrolling. Native elements until the design system
 * lands (#15).
 */
export function closingScreen({ sequence, reducedMotion, onFinish }: ClosingScreenOptions): HTMLElement {
  const section = document.createElement('section');
  section.className = 'panel closing';
  section.setAttribute('aria-labelledby', 'closing-title');
  let page = 0;
  let rendered: ClosingStep | null = null;

  const render = () => {
    if (rendered !== sequence.step) page = 0;
    rendered = sequence.step;
    section.dataset.step = sequence.step;
    const credits = sequence.step === 'CREDITOS';
    const caption = CAPTIONS[sequence.step];
    section.innerHTML = `
      <p class="tactical">${t('ui.screen.cierre')}</p>
      <h1 id="closing-title" class="display-mono" data-field="step">${t(`ui.cierre.step.${sequence.step}` as StringKey)}</h1>
      ${credits ? creditsMarkup(reducedMotion, page) : `
      <div class="closing-scene" data-field="scene">${marks(sequence.step)}${caption ? `<p class="body" data-field="caption">${t(caption)}</p>` : ''}</div>
      <ul class="closing-lines">${sequence.lines
        .map((line) => `<li data-field="line"><span class="tactical">${t(line.speaker)}</span> <span class="body" data-field="text">${t(line.text)}</span></li>`)
        .join('')}</ul>`}
      <div class="slot-actions">
        <button type="button" class="button" data-action="skip">${t('ui.cierre.skip')}</button>
      </div>`;
    section.querySelector<HTMLButtonElement>('[data-action="skip"]')!.addEventListener('click', () => sequence.skip());
    section.querySelector('[data-action="next"]')?.addEventListener('click', () => {
      page += 1;
      render();
      section.querySelector<HTMLButtonElement>('[data-action="next"], [data-action="finish"]')?.focus();
    });
    section.querySelector('[data-action="finish"]')?.addEventListener('click', () => sequence.skip());
    // "Saltar" is the first thing the keyboard reaches: it has the focus when a beat starts.
    queueMicrotask(() => section.querySelector<HTMLButtonElement>('[data-action="skip"]')?.focus());
  };

  const stop = sequence.subscribe(() => {
    if (sequence.done) {
      cleanup();
      onFinish();
    } else render();
  });
  const onKey = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    sequence.skip();
  };
  window.addEventListener('keydown', onKey);
  // Time passes in the sequence while the screen is on; with reduced motion the credits wait for the player to page.
  const timer = setInterval(() => {
    if (!section.isConnected) return cleanup();
    if (reducedMotion && sequence.step === 'CREDITOS') return;
    sequence.tick(TICK_MS);
  }, TICK_MS);
  function cleanup() {
    clearInterval(timer);
    window.removeEventListener('keydown', onKey);
    stop();
  }

  render();
  return section;
}
